import React, { useEffect, useContext } from "react";
import PropTypes from "prop-types";

import { Notebook } from "@learningblocks/simulations/shared-components";
import { Utilities } from "@learningblocks/exp-constants";
const { resetScroll } = Utilities;
import Modal from "react-modal";
import { PopoutFooter } from "@learningblocks/simulations/shared-components";
import { ProgressBarBlock } from "@learningblocks/simulations/hotspot-sim-components";
import { ImageDiscoveryCanvas } from "@learningblocks/interactives/interactive-infographic-components";
import { LogContext } from "@learningblocks/shared-components/lib/EventLogger/Log";
import { sendLog } from "@learningblocks/shared-components/lib/EventLogger/utils";

const appElement = document.getElementById("container");
if (appElement) {
  Modal.setAppElement("#container");
}

export function HotspotCommonSection(props) {
  const logContext = useContext(LogContext);

  useEffect(() => {
    resetScroll("book_content_body");
  }, [props.currentMenu]);

  const isLastChapter = props.nextMenuValue === undefined;
  const isFirstChapter = props.prevMenuValue === undefined;

  const contentBodyClass = `book_content_body${isLastChapter ? " lastChapter" : ""}${props.currentMenu.simData ? " hotspot_content_body" : ""}`;

  const handleSceneUpdate = (hotspots, triggerLog, currentView) => {
    props.updateSceneData({
      menu: props.currentMenu.value,
      hotspots: hotspots,
      triggerLog: triggerLog,
      currentView: currentView,
    });
  };

  const handleUpdateProgress = (menuValue) => {
    const menuValueObject = props.menuList.find(
      (item) => item.value === menuValue
    );
    if (
      menuValueObject &&
      menuValueObject.activitiesCount === 0 &&
      menuValueObject.currentProgress === 0 &&
      menuValueObject.maxProgress > menuValueObject.currentProgress
    ) {
      const newProgress = menuValueObject.currentProgress + 1;
      const isSectionComplete = menuValueObject.maxProgress === newProgress;

      props.updateProgress({
        menu: menuValue,
        menuProgress: newProgress,
        overallProgress: props.currentProgress + 1,
      });

      if (isSectionComplete && logContext) {
        const logData = {
          event_type: `sim_${menuValue || ""}_section_completed`,
          event_schema: "track",
          sim_id: logContext.sim_id,
          sim_name: logContext.sim_name,
          sim_session_id: logContext.sim_session_id,
          level: logContext.level,
          section: (menuValue || "").toLowerCase(),
        };
        sendLog(logData);
      }
    }
  };

  if (isLastChapter) {
    handleUpdateProgress(props.currentMenu.value);
  }

  const onClickNextSection = () => {
    handleUpdateProgress(props.currentMenu.value);
    props.goToSection(props.nextMenuValue);
  };

  const updateHotspotsProgress = (triggeredActionsOverall) => {
    const totalProgressMade = props.menuList
      .filter((item) => item.value !== props.currentMenu.value)
      .reduce((sum, item) => sum + item.currentProgress, 0);
    props.updateProgress({
      menu: props.currentMenu.value,
      menuProgress: triggeredActionsOverall,
      overallProgress: totalProgressMade + triggeredActionsOverall,
    });
  };

  const forceStateChangers = () => {
    const TYPES = {
      ABSOLUTE: "ABSOLUTE",
      PREDICTION: "PREDICTION",
      STATE_RAND: "STATE_RAND",
    };

    const forceStateChangers = props.currentMenu.forceStateChangers || [];
    const compiledForceStateChangers = [];
    forceStateChangers.forEach((forceStateChanger) => {
      const compiledForceStateChanger = {
        id: forceStateChanger.hotspotId,
        state: 1, // Default state
      };

      switch (forceStateChanger.refType) {
        case TYPES.ABSOLUTE:
          compiledForceStateChanger.state = forceStateChanger.values[0];
          break;

        case TYPES.PREDICTION:
          if (
            props.cscq &&
            props.cscq[forceStateChanger.id] &&
            Array.isArray(props.cscq[forceStateChanger.id].disabled) &&
            props.cscq[forceStateChanger.id].disabled.length > 0
          ) {
            const selectedIndex = props.cscq[forceStateChanger.id].disabled[0];
            compiledForceStateChanger.state =
              forceStateChanger.values[selectedIndex];
          }
          break;

        case TYPES.STATE_RAND:
          if (
            forceStateChanger.key &&
            props.generatedVariables &&
            Object.prototype.hasOwnProperty.call(
              props.generatedVariables,
              forceStateChanger.key
            )
          ) {
            compiledForceStateChanger.state =
              props.generatedVariables[forceStateChanger.key];
          }
          break;

        default:
          break;
      }
      compiledForceStateChangers.push(compiledForceStateChanger);
    });

    return compiledForceStateChangers;
  };

  const onClickPrevSection = () => props.goToSection(props.prevMenuValue);

  return (
    <div className="hotspot_book_index_container" aria-hidden={props.collapsed}>
      <Notebook.IndexBlock className="book_index">
        <Notebook.Header className="book_index_header hotspot_navbar_header">
          <ProgressBarBlock
            className="index-titleBlock"
            title={props.title}
            maxProgress={props.maxProgress}
            currentProgress={props.currentProgress}
            progress={props.progress}
            menuList={props.menuList}
          />
        </Notebook.Header>
        <Notebook.MenuList
          className="book_index_chapters hotspot_book_index_chapters"
          menuList={props.menuList}
          onSelect={props.goToSection}
          activeMenuClass="chapter-active"
          lockedMenuClass="chapter-locked"
          inactiveMenuClass="chapter"
          displayProgress={props.displayProgress}
          enableActiveGhost
        />
      </Notebook.IndexBlock>
      <Notebook.ContentBlock className="book_content">
        <Notebook.ContentBody className={contentBodyClass}>
          {props.currentMenu.simData ? (
            <ImageDiscoveryCanvas
              key={props.currentMenu.simData.mainView}
              sceneData={props.currentMenu.simData}
              preview={false}
              standalone={false}
              trackProgress={true}
              isDiabledBlurOnSmall={true}
              updateProgress={updateHotspotsProgress}
              forceStateChangers={forceStateChangers()}
              handleSceneUpdate={handleSceneUpdate}
            />
          ) : (
            <Notebook.ContentReadArea className="read_block">
              <Notebook.ContentTitle
                className="content_title"
                title={props.currentMenu.title}
                number={props.currentMenu.number}
              />
              <Notebook.ContentComponents
                components={props.components}
                className="content_component"
              />
            </Notebook.ContentReadArea>
          )}
        </Notebook.ContentBody>
        <PopoutFooter
          lang={"en"}
          prevSection={onClickPrevSection}
          isFirstChapter={isFirstChapter}
          isLastChapter={isLastChapter}
          isSectionComplete={
            props.currentMenu?.maxProgress ===
            props.currentMenu?.currentProgress
          }
          section={props.currentMenu.title}
          nextSection={onClickNextSection}
        />
      </Notebook.ContentBlock>
    </div>
  );
}

HotspotCommonSection.propTypes = {
  /**
   * Components in current section to be displayed in Notebook
   */
  components: PropTypes.arrayOf(PropTypes.object),
  /**
   * Details of currently active menu in menu list
   */
  currentMenu: PropTypes.shape({
    /**
     * Index of menu
     */
    number: PropTypes.number,
    /**
     * Title of menu
     */
    title: PropTypes.string,
    /**
     * Value of menu
     */
    value: PropTypes.string,
  }),
  /**
   * Description to be displayed in Notebook Header
   */
  description: PropTypes.string,
  /**
   * Function to go to next section
   */
  goToSection: PropTypes.func,
  /**
   * Function to go to next section
   */
  updateProgress: PropTypes.func,
  /**
   * List of Menu Items
   */
  menuList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Title to display in  menu
       */
      title: PropTypes.string.isRequired,
      /**
       * Boolean specifying if menu is selected
       */
      current: PropTypes.bool.isRequired,
      /**
       * Index number of menu
       */
      number: PropTypes.number.isRequired,
      /**
       * Unique string value of the menu
       */
      value: PropTypes.string.isRequired,
      /**
       * Boolean set when section is visited
       */
      visited: PropTypes.bool.isRequired,
      /**
       * Boolean to check if progress should be displayed or not
       */
      displayProgress: PropTypes.bool.isRequired,
    })
  ),
  /**
   * Value for next-in-line menu
   * NONE value mens no more next section
   */
  nextMenuValue: PropTypes.oneOf([
    "CONTEXT",
    "MATERIALS",
    "PREDICTIONS",
    "TECHNIQUES",
    "DESIGN",
    "PROTOCOL",
    "PROBLEM_SOLVING",
    "RESULTS",
    "REFLECTION",
    "TROUBLESHOOTING",
    "SUMMARY",
    "NONE",
    "PREDICTIONS1",
    "PROTOCOL1",
    "RESULTS1",
    "PREDICTIONS2",
    "PROTOCOL2",
    "RESULTS2",
  ]),
  /**
   * Value for next-in-line menu
   * NONE value mens no more next section
   */
  prevMenuValue: PropTypes.oneOf([
    "CONTEXT",
    "MATERIALS",
    "PREDICTIONS",
    "TECHNIQUES",
    "DESIGN",
    "PROTOCOL",
    "PROBLEM_SOLVING",
    "RESULTS",
    "REFLECTION",
    "TROUBLESHOOTING",
    "SUMMARY",
    "NONE",
    "PREDICTIONS1",
    "PROTOCOL1",
    "RESULTS1",
    "PREDICTIONS2",
    "PROTOCOL2",
    "RESULTS2",
  ]),
  /**
   * Function to set new isNotebookVisible value
   */
  setNotebookVisible: PropTypes.func,
  /**
   * Title of the Content to display at header
   */
  title: PropTypes.string,
  /**
   * SimTpe of the Content
   */
  simType: PropTypes.string,
};
